/**
 * GovernanceStrip Component
 * 
 * Always-visible horizontal strip showing governance indicators.
 * Displays human approval requirements, Decision Guardian status,
 * audit log recording, and data boundary controls.
 * 
 * Style: Clinical, monospace-like labels, professional tone.
 */

import React from 'react';
import { Shield, FileText, Database, User, Sparkles } from 'lucide-react';

interface GovernanceStripProps {
    humanApprovalRequired: boolean;
    guardianActive: boolean;
    auditRecording: boolean;
    surpriseActive?: boolean;
    onDataBoundariesClick?: () => void;
}

export const GovernanceStrip: React.FC<GovernanceStripProps> = ({
    humanApprovalRequired,
    guardianActive,
    auditRecording,
    surpriseActive = false,
    onDataBoundariesClick
}) => {
    return (
        <div className="w-full bg-hig-bg-tertiary border-b border-hig-border">
            <div className="max-w-7xl mx-auto px-4 py-2">
                <div className="flex items-center justify-between gap-4 text-xs font-medium">
                    {/* Left side - Status indicators */}
                    <div className="flex items-center gap-6">
                        {/* Human Approval Required */}
                        <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-hig-text-tertiary" />
                            <span className="text-hig-text-tertiary uppercase tracking-wide">
                                Human Approval
                            </span>
                            <StatusBadge
                                active={humanApprovalRequired}
                                activeLabel="Required"
                                inactiveLabel="Optional"
                            />
                        </div>

                        {/* Decision Guardian Status */}
                        <div className="flex items-center gap-2">
                            <Shield className="w-3.5 h-3.5 text-hig-text-tertiary" />
                            <span className="text-hig-text-tertiary uppercase tracking-wide">
                                Decision Guardian
                            </span>
                            <StatusBadge
                                active={guardianActive}
                                activeLabel="Active"
                                inactiveLabel="Inactive"
                                activeColor="green"
                            />
                        </div>

                        {/* Audit Log Recording */}
                        <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-hig-text-tertiary" />
                            <span className="text-hig-text-tertiary uppercase tracking-wide">
                                Audit Log
                            </span>
                            <StatusBadge
                                active={auditRecording}
                                activeLabel="Recording"
                                inactiveLabel="Paused"
                                activeColor="green"
                            />
                        </div>

                        {/* Titans Surprise Detection */}
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-hig-text-tertiary" />
                            <span className="text-hig-text-tertiary uppercase tracking-wide">
                                Surprise
                            </span>
                            <StatusBadge
                                active={surpriseActive}
                                activeLabel="Active"
                                inactiveLabel="Standby"
                                activeColor="purple"
                            />
                        </div>
                    </div>

                    {/* Right side - Data boundaries link */}
                    <button
                        onClick={onDataBoundariesClick}
                        className="flex items-center gap-2 text-hig-text-secondary hover:text-hig-accent transition-colors"
                    >
                        <Database className="w-3.5 h-3.5" />
                        <span className="uppercase tracking-wide">Data Boundaries</span>
                        <span className="text-hig-text-tertiary">Edit</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// STATUS BADGE SUB-COMPONENT
// ============================================

interface StatusBadgeProps {
    active: boolean;
    activeLabel: string;
    inactiveLabel: string;
    activeColor?: 'blue' | 'green' | 'purple';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
    active,
    activeLabel,
    inactiveLabel,
    activeColor = 'blue'
}) => {
    const colorClasses = active
        ? activeColor === 'green'
            ? 'bg-green-500/20 text-green-400 border-green-500/30'
            : activeColor === 'purple'
                ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                : 'bg-hig-accent/20 text-hig-accent border-hig-accent/30'
        : 'bg-hig-bg-secondary text-hig-text-tertiary border-hig-border';

    return (
        <span
            className={`
        px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider
        border ${colorClasses}
      `}
        >
            {active ? activeLabel : inactiveLabel}
        </span>
    );
};

export default GovernanceStrip;
